import { Editor, Range, Element as SlateElement, Transforms } from 'slate';

import { CustomEditor } from '../types';
import { serialize } from '../utils';

export const withMaxLength = (
  editor: CustomEditor,
  currentValueRef: React.RefObject<string | undefined>,
  maxLength: number
) => {
  const { insertText, insertData } = editor;

  // 重写文本插入
  editor.insertText = (text, options) => {
    if (!maxLength) {
      insertText(text, options);
      return;
    }

    // 计算选中内容的长度
    let selectedLength = 0;
    if (editor.selection && !Range.isCollapsed(editor.selection)) {
      const fragment = Editor.fragment(editor, editor.selection);
      selectedLength = serialize(fragment).length;
    }

    // 计算插入后的总长度
    const currentLength = currentValueRef.current?.length || 0;
    const finalLength = currentLength - selectedLength + text.length;

    if (finalLength > maxLength) {
      // 如果超出长度限制,只插入能容纳的部分
      const availableSpace = maxLength - (currentLength - selectedLength);
      if (availableSpace > 0) {
        // 截取可以插入的文本长度
        const truncatedText = text.slice(0, availableSpace);
        insertText(truncatedText, options);
      } else {
        //到达最大长度后，仍然能够触发合成输入，合成输入的值会在视图中展示（但实际值被限制住了），这里的操作用于强制刷新编辑器内部视图
        Transforms.insertText(editor, ' ', options);
        Transforms.delete(editor, { distance: 1, reverse: true });
      }
      return;
    }

    insertText(text, options);
  };

  // 重写粘贴处理
  editor.insertData = (data: DataTransfer) => {
    if (!maxLength) {
      insertData(data);
      return;
    }
    const currentLength = currentValueRef.current?.length || 0;

    // 优先处理 Slate 格式的数据
    const slateData = data.getData('application/x-slate-fragment');
    if (slateData) {
      try {
        const fragment = JSON.parse(decodeURIComponent(window.atob(slateData)));
        const fragmentText = serialize(fragment);
        const currentLength = currentValueRef.current?.length || 0;

        // 计算选中内容的长度
        let selectedLength = 0;
        if (editor.selection && !Range.isCollapsed(editor.selection)) {
          const selectedFragment = Editor.fragment(editor, editor.selection);
          selectedLength = serialize(selectedFragment).length;
        }

        // 检查长度限制
        const finalLength = currentLength - selectedLength + fragmentText.length;

        if (finalLength > maxLength) {
          // 如果超出限制，需要智能截断
          const availableSpace = maxLength - (currentLength - selectedLength);
          if (availableSpace <= 0) return;

          // 智能截断片段
          const truncatedFragment = truncateSlateFragment(fragment, availableSpace);

          // 删除选中内容
          if (editor.selection && !Range.isCollapsed(editor.selection)) {
            Transforms.delete(editor);
          }

          // 插入截断后的片段
          Transforms.insertFragment(editor, truncatedFragment);
          return;
        }
      } catch (error) {
        console.error('Error processing slate fragment:', error);
      }
    }

    // 降级到纯文本处理
    const text = data.getData('text/plain');
    if (text) {
      // 如果有选中内容，计算选中内容的长度
      let selectedLength = 0;
      if (editor.selection && !Range.isCollapsed(editor.selection)) {
        const fragment = Editor.fragment(editor, editor.selection);
        selectedLength = serialize(fragment).length;
      }

      // 计算粘贴后的总长度
      const finalLength = currentLength - selectedLength + text.length;

      if (finalLength > maxLength) {
        // 如果超出长度限制，只插入能容纳的部分
        const availableSpace = maxLength - (currentLength - selectedLength);
        if (availableSpace > 0) {
          // 截取可以插入的文本长度
          const truncatedText = text.slice(0, availableSpace);
          editor.insertText(truncatedText);
        }
        return;
      }
    }

    // 如果没有超出限制，正常插入
    insertData(data);
  };

  editor.maxLength = maxLength;
  editor.currentValueRef = currentValueRef;
  return editor;
};

const truncateSlateFragment = (fragment: any[], maxLength: number): any[] => {
  let currentLength = 0;
  const result = [];

  for (const node of fragment) {
    if (SlateElement.isElement(node)) {
      if (node.type === 'mention' && node.tag) {
        // mention 标签作为整体处理
        const tagLength = node.tag.value.length;
        if (currentLength + tagLength <= maxLength) {
          result.push({ ...node });
          currentLength += tagLength;
        } else {
          break;
        }
      } else {
        // 递归处理其他元素
        const children = truncateSlateFragment(node.children, maxLength - currentLength);
        if (children.length > 0) {
          result.push({ ...node, children });
          currentLength += serialize(children).length;
        }
      }
    } else {
      // 文本节点
      const text = node.text;
      const remainingSpace = maxLength - currentLength;
      if (remainingSpace <= 0) break;

      if (text.length <= remainingSpace) {
        result.push({ ...node });
        currentLength += text.length;
      } else {
        result.push({ ...node, text: text.slice(0, remainingSpace) });
        currentLength += remainingSpace;
        break;
      }
    }
  }

  return result;
};
